import React, { useState, useMemo } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ReviewerQueuePanel = ({
  assignments,
  selectedSubmissionId,
  onSelectPaper,
  onBulkDownload,
  loading = false
}) => {
  const [downloading, setDownloading] = useState(false);

  const handleBulkDownload = async () => {
    if (!onBulkDownload || downloading) return;
    setDownloading(true);
    try {
      await onBulkDownload();
    } finally {
      setDownloading(false);
    }
  };
  const [filterStatus, setFilterStatus] = useState('all'); // all, pending, completed
  const [sortBy, setSortBy] = useState('deadline'); // deadline, title, status

  // Filter assignments based on status
  const filteredAssignments = useMemo(() => {
    return assignments?.filter(assignment => {
      if (filterStatus === 'all') return true;
      if (filterStatus === 'pending') return assignment?.isDraft;
      if (filterStatus === 'completed') return !assignment?.isDraft;
      return true;
    }) || [];
  }, [assignments, filterStatus]);

  // Sort assignments
  const sortedAssignments = useMemo(() => {
    const sorted = [...filteredAssignments];
    
    switch (sortBy) {
      case 'deadline':
        return sorted.sort((a, b) => {
          const deadlineA = new Date(a?.reviewDeadline || '9999');
          const deadlineB = new Date(b?.reviewDeadline || '9999');
          return deadlineA - deadlineB;
        });
      case 'title':
        return sorted.sort((a, b) => (a?.paperTitle || '')?.localeCompare(b?.paperTitle || ''));
      case 'status':
        return sorted.sort((a, b) => {
          const statusOrder = { 'pending': 0, 'in-progress': 1, 'completed': 2 };
          return (statusOrder[a?.status] ?? 3) - (statusOrder[b?.status] ?? 3);
        });
      default:
        return sorted;
    }
  }, [filteredAssignments, sortBy]);

  // Calculate deadline status
  const getDeadlineStatus = (deadline) => {
    if (!deadline) return 'no-deadline';
    
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const daysLeft = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft <= 0) return 'overdue';
    if (daysLeft <= 3) return 'critical';
    if (daysLeft <= 7) return 'urgent';
    return 'normal';
  };

  const getDeadlineText = (deadline) => {
    if (!deadline) return 'No deadline';
    
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const daysLeft = Math.ceil((deadlineDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft <= 0) return `Overdue by ${Math.abs(daysLeft)} days`;
    if (daysLeft === 1) return 'Due tomorrow';
    return `${daysLeft} days left`;
  };

  return (
    <div className="flex flex-col bg-card border border-border rounded-lg shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Review Queue</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {sortedAssignments?.length} paper{sortedAssignments?.length !== 1 ? 's' : ''}
          </p>
        </div>
        {assignments?.some(a => a?.pdfUrl) && (
          <Button
            /* Secondary: the bulk convenience, not the main act. Also the shared
               Button now rather than hand-rolled classes, which is how it
               drifted to primary colouring in the first place. */
            variant="outline"
            size="xs"
            onClick={handleBulkDownload}
            loading={downloading}
            iconName="Download"
            iconPosition="left"
          >
            {downloading ? 'Zipping…' : 'Download all'}
          </Button>
        )}
      </div>

      {/* Filters & Sort */}
      <div className="px-3 py-3 border-b border-border space-y-2">
        {/* Filter */}
        <div className="flex gap-1.5">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-2.5 py-1 text-xs font-medium transition-colors ${
              filterStatus === 'all'
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            All ({assignments?.length})
          </button>
          <button
            onClick={() => setFilterStatus('pending')}
            className={`px-2.5 py-1 text-xs font-medium transition-colors ${
              filterStatus === 'pending'
                ? 'bg-warning text-warning-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Pending ({assignments?.filter(a => a?.isDraft)?.length})
          </button>
          <button
            onClick={() => setFilterStatus('completed')}
            className={`px-2.5 py-1 text-xs font-medium transition-colors ${
              filterStatus === 'completed'
                ? 'bg-success text-success-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            Done ({assignments?.filter(a => !a?.isDraft)?.length})
          </button>
        </div>

        {/* Sort */}
        <Select
          value={sortBy}
          onChange={setSortBy}
          options={[
            { value: 'deadline', label: 'Sort by deadline' },
            { value: 'title', label: 'Sort by title' },
            { value: 'status', label: 'Sort by status' },
          ]}
        />
      </div>

      {/* Papers List */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-muted-foreground">
              <Icon name="Loader" size={24} className="animate-spin mx-auto mb-2" />
              <p className="text-sm">Loading assignments...</p>
            </div>
          </div>
        ) : sortedAssignments?.length === 0 ? (
          <div className="flex items-center justify-center h-full p-4">
            <div className="text-center text-muted-foreground">
              <Icon name="InboxIcon" size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No papers in this category</p>
            </div>
          </div>
        ) : (
          <div className="space-y-1.5 p-3">
            {sortedAssignments?.map(assignment => {
              const deadlineStatus = getDeadlineStatus(assignment?.reviewDeadline);
              const isSelected = selectedSubmissionId === assignment?.submissionId;

              const statusColorMap = {
                'pending': 'bg-muted text-muted-foreground border-border',
                'in-progress': 'bg-warning/10 border-warning text-warning-foreground',
                'completed': 'bg-success/10 border-success text-success-foreground'
              };

              const deadlineColorMap = {
                'overdue': 'bg-error/10 border-error text-error',
                'critical': 'bg-error/20 border-error/50 text-error',
                'urgent': 'bg-warning/10 border-warning text-warning-foreground',
                'normal': 'bg-muted text-muted-foreground',
                'no-deadline': 'bg-muted text-muted-foreground'
              };

              return (
                <button
                  key={assignment?.submissionId}
                  onClick={() => onSelectPaper(assignment?.submissionId)}
                  className={`w-full text-left p-2.5 rounded-md border transition-all ${
                    isSelected
                      ? 'bg-primary/10 border-primary shadow-sm'
                      : 'bg-background border-border hover:border-primary/40 hover:bg-muted/30'
                  }`}
                >
                  {/* Status + Progress */}
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span
                      className={`px-1.5 py-0.5 text-[10px] font-semibold ${
                        statusColorMap[assignment?.status] || statusColorMap['in-progress']
                      }`}
                    >
                      {assignment?.status === 'pending' ? 'New' : assignment?.status === 'in-progress' ? 'In Progress' : 'Completed'}
                    </span>
                    {assignment?.progress !== undefined && assignment?.progress > 0 && (
                      <span className="text-[10px] text-muted-foreground ml-auto">
                        {assignment?.progress}%
                      </span>
                    )}
                  </div>

                  {/* Title */}
                  <h4 className="font-medium text-foreground text-xs line-clamp-2 mb-1">
                    {assignment?.paperTitle}
                  </h4>

                  {/* Conference */}
                  <p className="text-[10px] text-muted-foreground mb-1.5">
                    {assignment?.conferenceName}
                    {assignment?.trackName && ` • ${assignment?.trackName}`}
                  </p>

                  {/* Deadline */}
                  <div
                    className={`px-1.5 py-0.5 text-[10px] font-medium w-fit ${
                      deadlineColorMap[deadlineStatus]
                    }`}
                  >
                    {getDeadlineText(assignment?.reviewDeadline)}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer Stats */}
      {sortedAssignments?.length > 0 && (
        <div className="px-3 py-2 border-t border-border text-[10px] text-muted-foreground flex gap-3">
          <span>Pending: <strong>{assignments?.filter(a => a?.isDraft)?.length}</strong></span>
          <span>Done: <strong>{assignments?.filter(a => !a?.isDraft)?.length}</strong></span>
        </div>
      )}
    </div>
  );
};

export default ReviewerQueuePanel;
